import { Injectable } from '@angular/core';
import { getObjectPropValue } from '@shared/utils';

export interface PositionSyncElement {
  pageNum?: number;
  y1?: number | null;
  y2?: number | null;
}

export interface PositionSyncMatch<T> {
  pageNum?: number;
  element?: T;
}

@Injectable({ providedIn: 'root' })
export class DocumentPositionSyncService {
  private readonly minVisiblePagePx = 10;
  private readonly switchVisibleRatio = 0.9;

  isEnabled(uiConfig: object | undefined): boolean {
    return getObjectPropValue(uiConfig, 'tiles_config.follow_pdf_viewer_scroll') === true;
  }

  /**
   * Keeps the current page until it is (almost) out of view or another page takes up
   * at least `switchVisibleRatio` of the viewer; undefined when no page can be determined.
   */
  getPageToDisplay(viewer: any, visiblePages: any[]): number | undefined {
    const container = viewer?.container as HTMLElement | undefined;
    if (!container || !visiblePages?.length) {
      return undefined;
    }

    const currentPageNum: number | undefined = viewer.currentPageNumber;
    const viewTop = container.scrollTop;
    const viewHeight = container.clientHeight;
    const viewBottom = viewTop + viewHeight;
    const containerTop = container.getBoundingClientRect().top;

    // Page bounds in scroll (content) coordinates.
    const toBounds = (page: any) => {
      const pageDiv: HTMLElement | undefined = page?.view?.div ?? page?.div;
      if (!pageDiv) {
        return undefined;
      }
      const rect = pageDiv.getBoundingClientRect();
      const top = rect.top - containerTop + viewTop;
      return { id: page.id as number, top, bottom: top + rect.height, height: rect.height };
    };

    // 1. Parameters of the current page.
    const current = toBounds(visiblePages.find(page => page.id === currentPageNum));
    const currentVisiblePx = current
      ? Math.min(current.bottom, viewBottom) - Math.max(current.top, viewTop)
      : 0;

    // 2. Most visible other page.
    let best: { id: number; visiblePx: number; ratio: number } | undefined;
    for (const page of visiblePages) {
      if (page.id === currentPageNum) {
        continue;
      }
      const bounds = toBounds(page);
      if (!bounds) {
        continue;
      }
      const visiblePx = Math.min(bounds.bottom, viewBottom) - Math.max(bounds.top, viewTop);
      if (visiblePx <= 0) {
        continue;
      }
      // Pages shorter than the viewport can never fill it, so compare against the smaller size.
      const ratio = visiblePx / Math.min(bounds.height, viewHeight);
      if (!best || visiblePx > best.visiblePx) {
        best = { id: bounds.id, visiblePx, ratio };
        console.log('New best page:', bounds.id, 'Visible pixels:', visiblePx, 'Ratio:', ratio);
      }
    }

    if (!current || currentVisiblePx < this.minVisiblePagePx) {
      return best?.id ?? currentPageNum;
    }
    if (!best) {
      return currentPageNum;
    }

    const atScrollEnd = viewBottom >= container.scrollHeight - 1;
    const nextIsBelow = best.id > (currentPageNum as number);
    const switchNow = best.ratio >= this.switchVisibleRatio || (atScrollEnd && nextIsBelow);
    return switchNow ? best.id : currentPageNum;
  }

  sortByPagePosition<T extends PositionSyncElement>(elements: T[]): T[] {
    return [...elements].sort((a, b) => {
      const pageDifference = (a.pageNum ?? Number.POSITIVE_INFINITY) -
        (b.pageNum ?? Number.POSITIVE_INFINITY);
      if (pageDifference !== 0) {
        return pageDifference;
      }

      const y1Difference = (a.y1 ?? Number.POSITIVE_INFINITY) -
        (b.y1 ?? Number.POSITIVE_INFINITY);
      if (y1Difference !== 0) {
        return y1Difference;
      }

      return (a.y2 ?? Number.POSITIVE_INFINITY) -
        (b.y2 ?? Number.POSITIVE_INFINITY);
    });
  }

  findClosestElement<T extends PositionSyncElement>(
    viewer: any,
    elements: T[]
  ): PositionSyncMatch<T> {
    const container = viewer?.container as HTMLElement | undefined;
    const pages: any[] = viewer?._pages ?? [];
    if (!container || !pages.length) {
      return {};
    }

    // Top edge of the visible PDF area.
    const visibleTop = container.getBoundingClientRect().top;
    const pageNum: number | undefined = viewer.currentPageNumber;
    if (pageNum == null) {
      return {};
    }

    const candidates = this.sortByPagePosition(
      elements.filter(element => element.y1 != null && element.pageNum === pageNum)
    );
    const element = candidates.find(
      candidate => this.getElementViewportY(pages, candidate) >= visibleTop
    );

    return { pageNum, element };
  }

  private getElementViewportY(pages: any[], element: PositionSyncElement): number {
    const page = pages[(element.pageNum ?? 0) - 1];
    const pageView = page?.pdfPage?._pageInfo?.view;
    const pageHeight = pageView ? Math.abs(pageView[3] - pageView[1]) : 0;
    const y1 = element.y1;
    if (!page?.div || !pageHeight || y1 == null) {
      return Number.NEGATIVE_INFINITY;
    }

    const pageRect = page.div.getBoundingClientRect();
    return pageRect.top + Math.min(y1, element.y2 ?? y1) * pageRect.height / pageHeight;
  }

  scrollElementToTop(container: HTMLElement, element: HTMLElement): void {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const targetTop = container.scrollTop + elementRect.top - containerRect.top;
    const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);

    container.scrollTo({
      top: Math.max(0, Math.min(targetTop, maxScrollTop)),
      behavior: 'smooth'
    });
  }
}
